import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { adminAPI } from '../../services/api';
import { Plus, Edit2, Trash2, Layers, X, Check } from 'lucide-react';
import { toast } from '../../components/common/Toast';

const AdminSubcategories = () => {
  const { isDarkMode } = useTheme();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category_id: '', name: '', slug: '', description: '', sort_order: 0, is_active: true });

  const load = async () => {
    try {
      const [catRes, subRes] = await Promise.all([
        adminAPI.getCategories(),
        adminAPI.getSubcategories(),
      ]);
      setCategories(catRes.data);
      setItems(subRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ category_id: categories[0]?.id || '', name: '', slug: '', description: '', sort_order: 0, is_active: true });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ category_id: item.category_id, name: item.name, slug: item.slug, description: item.description || '', sort_order: item.sort_order, is_active: item.is_active });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.slug || !form.category_id) return toast.warning('Complete todos los campos');
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateSubcategory(editing.id, form);
        toast.success('Subcategoría actualizada');
      } else {
        await adminAPI.createSubcategory(form);
        toast.success('Subcategoría creada');
      }
      setShowModal(false);
      load();
    } catch (e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta subcategoría?')) return;
    try {
      await adminAPI.deleteSubcategory(id);
      toast.success('Subcategoría eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '—';

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" /></div>;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Subcategorías</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{items.length} subcategorías registradas</p>
        </div>
        <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl text-sm font-medium shadow-lg shadow-orange-500/20"
        ><Plus className="w-4 h-4" /> Nueva Subcategoría</motion.button>
      </div>

      <div className={`${isDarkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm rounded-3xl border ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-sm overflow-hidden`}>
        <table className="w-full text-sm">
          <thead><tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <th className="text-left px-6 py-4 font-medium">Nombre</th>
            <th className="text-left px-6 py-4 font-medium">Slug</th>
            <th className="text-left px-6 py-4 font-medium">Categoría</th>
            <th className="text-center px-6 py-4 font-medium">Orden</th>
            <th className="text-center px-6 py-4 font-medium">Activo</th>
            <th className="text-right px-6 py-4 font-medium">Acciones</th>
          </tr></thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={item.id} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'} hover:bg-orange-50/30 dark:hover:bg-orange-900/10`}>
                <td className="px-6 py-4 font-medium">{item.name}</td>
                <td className={`px-6 py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.slug}</td>
                <td className="px-6 py-4">{getCategoryName(item.category_id)}</td>
                <td className="px-6 py-4 text-center">{item.sort_order}</td>
                <td className="px-6 py-4 text-center">{item.is_active ? <Check className="w-4 h-4 text-green-500 mx-auto" /> : <X className="w-4 h-4 text-red-500 mx-auto" />}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => openEdit(item)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl mr-1"><Edit2 className="w-4 h-4 text-blue-500" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)}>
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl p-8 w-full max-w-lg mx-4 shadow-2xl`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{editing ? 'Editar' : 'Nueva'} Subcategoría</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Categoría</label>
                <select value={form.category_id} onChange={e => setForm({...form, category_id: e.target.value})}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              {['name','slug'].map(f => (
                <div key={f}>
                  <label className={`block text-sm mb-1 capitalize ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{f}</label>
                  <input value={form[f]} onChange={e => setForm({...form, [f]: e.target.value})}
                    className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                </div>
              ))}
              <div>
                <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Descripción</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
                  className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Orden</label>
                  <input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})}
                    className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`} />
                </div>
                <div>
                  <label className={`block text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Activo</label>
                  <select value={form.is_active} onChange={e => setForm({...form, is_active: e.target.value === 'true'})}
                    className={`w-full px-4 py-2.5 rounded-2xl border text-sm ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'}`}>
                    <option value={true}>Sí</option>
                    <option value={false}>No</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                onClick={handleSave} disabled={saving}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-2xl text-sm font-medium disabled:opacity-50"
              >{saving ? 'Guardando...' : 'Guardar'}</motion.button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-2xl text-sm">Cancelar</button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default AdminSubcategories;
